// utils/chatUtils.ts
import { useLogger } from '@/composables/useLogger'
import type { UserLocation } from '@/types/chat' // Import needed type
import type { ChatType } from '@/types/chatEnums' // Import needed type

const { warn, logError, log } = useLogger() // Logger needed for getUserLocation

/** Helper to parse timestamps consistently, handling potential UTC strings. */
export const parseTimestamp = (timestamp: string | Date | undefined): Date => {
  if (!timestamp) return new Date()
  // Ensure we handle potential UTC strings correctly if they don't have Z
  if (typeof timestamp === 'string' && !timestamp.endsWith('Z')) {
    // Basic check, might need more robust parsing depending on API formats
    if (timestamp.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/)) {
      return new Date(timestamp + 'Z')
    }
  }
  return timestamp instanceof Date ? timestamp : new Date(timestamp)
}

/** Generates a consistent chat ID string. */
export function generateChatId(type: ChatType, identifier: string): string {
  log(`🔧 generateChatId called with type: "${String(type)}", identifier: "${identifier}"`)
  if (!type) {
    logError('generateChatId: FATAL - type argument is null or undefined!', { type, identifier })
    return `error-invalid-type-${identifier}`
  }
  if (typeof type !== 'string') {
    logError(`generateChatId: FATAL - type argument is not a string! Received: ${String(type)}`, {
      type,
      identifier,
    })
    return `error-non-string-type-${identifier}`
  }
  // Ensure type is lowercase before concatenating
  return `${type.toLowerCase()}-${identifier}`
}

/** Attempts to get the user's current geolocation. Returns null if unavailable or denied. */
export async function getUserLocation(): Promise<UserLocation | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      warn('Geolocation is not supported by this browser.') // Use imported warn
      resolve(null)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
      },
      (error) => {
        logError('Error getting user location:', error.message) // Use imported logError
        resolve(null) // Resolve with null on error/denial
      },
      {
        enableHighAccuracy: false, // Lower accuracy is often faster and sufficient
        timeout: 5000, // Don't wait forever
        maximumAge: 600000, // Use cached position up to 10 minutes old
      }
    )
  })
}
